import ReactGA from 'react-ga4';
import Config from '@assetron/common/config';
import {
  GA_EVENTS,
  GaEventName,
} from '@assetron/common/constants/googleAnalyticsEvents';

const { appVersion, platform } = Config;

const GA_CONSENT_STORAGE_KEY = 'assetron_ga_consent';
const GA_CONSENT_GRANTED = 'granted';
const GA_CONSENT_DENIED = 'denied';
const MAX_STRING_PARAM_LENGTH = 200;

let isGaInitialized = false;
let initializedTrackingId: string | null = null;
let lastTrackedPagePath = '';
let gaExceptionTrackingCleanup: (() => void) | null = null;

export type GaEventParams = Record<
  string,
  string | number | boolean | null | undefined
>;

type GaScalar = string | number | boolean | null | undefined;

type KnownGaParamKey =
  | 'app'
  | 'page_path'
  | 'flow'
  | 'provider'
  | 'scope'
  | 'search_scope'
  | 'is_scoped_variant'
  | 'has_query'
  | 'query_length'
  | 'module_scope'
  | 'entity_scope'
  | 'clicked_element_type'
  | 'clicked_element_id'
  | 'has_intent'
  | 'country_id'
  | 'organization_id'
  | 'step_name'
  | 'attempt'
  | 'attempt_number'
  | 'status_code'
  | 'will_retry'
  | 'method'
  | 'endpoint_group'
  | 'endpoint_path'
  | 'latency_bucket_ms'
  | 'billing_provider'
  | 'is_create_organization_flow'
  | 'is_current_org_admin'
  | 'has_client_secret'
  | 'action'
  | 'plan_name'
  | 'plan_status'
  | 'subscription_source'
  | 'has_payment_method'
  | 'is_canceled'
  | 'return_url_present'
  | 'return_url_pathname'
  | 'portal_origin'
  | 'portal_url_present'
  | 'open_in_new_tab'
  | 'from_stripe_redirect'
  | 'checkout_session_present'
  | 'source'
  | 'destination'
  | 'failure_reason'
  | 'error_code'
  | 'purpose'
  | 'has_invite_details'
  | 'has_use_custom_token'
  | 'is_email_prefilled'
  | 'email_length'
  | 'password_length'
  | 'has_otp_code'
  | 'has_user_email'
  | 'sign_in_after_signup'
  | 'source_referrer'
  | 'button_name'
  | 'interaction_type';

type StrictGaEventParams = Partial<Record<KnownGaParamKey, GaScalar>>;

type RequiredEventParamMap = {
  [GA_EVENTS.AUTH_SIGNIN_SOCIAL_CLICK]: {
    provider: string;
  };
  [GA_EVENTS.AUTH_SIGNUP_SOCIAL_CLICK]: {
    provider: string;
  };
  [GA_EVENTS.SEARCH_SCOPE_CHANGED]: {
    scope: string;
  };
  [GA_EVENTS.SEARCH_QUERY_SUBMITTED]: {
    has_query: boolean;
    query_length: number;
  };
  [GA_EVENTS.SEARCH_SUGGESTION_CLICKED]: {
    clicked_element_type: string;
    clicked_element_id: string;
    has_intent: boolean;
  };
  [GA_EVENTS.SEARCH_RESULT_CLICKED]: {
    clicked_element_type: string;
    clicked_element_id: string;
    has_intent: boolean;
  };
  [GA_EVENTS.SEARCH_NO_RESULTS_VIEWED]: {
    query_length: number;
  };
  [GA_EVENTS.ONBOARDING_ORG_SETUP_SUBMIT]: {
    country_id: string;
  };
  [GA_EVENTS.BILLING_CUSTOMER_SESSION_ATTEMPT]: {
    attempt: number;
  };
  [GA_EVENTS.BILLING_CUSTOMER_SESSION_SUCCESS]: {
    attempt: number;
  };
  [GA_EVENTS.BILLING_CUSTOMER_SESSION_FAILURE]: {
    attempt: number;
  };
  [GA_EVENTS.ACCOUNT_SETTINGS_DELETE_PAGE_VIEWED]: {
    step_name: string;
  };
  [GA_EVENTS.ACCOUNT_SETTINGS_DELETE_WIZARD_OPENED]: {
    source: string;
    step_name: string;
  };
  [GA_EVENTS.ACCOUNT_SETTINGS_DELETE_WIZARD_CLOSED]: {
    step_name: string;
  };
  [GA_EVENTS.ACCOUNT_SETTINGS_DELETE_REQUEST_SUBMIT]: {
    step_name: string;
  };
  [GA_EVENTS.ACCOUNT_SETTINGS_DELETE_REQUEST_SUCCESS]: {
    step_name: string;
  };
  [GA_EVENTS.ACCOUNT_SETTINGS_DELETE_REQUEST_FAILURE]: {
    step_name: string;
    failure_reason: string;
  };
  [GA_EVENTS.ACCOUNT_SETTINGS_DELETE_RETURN_TO_SIGNIN_CLICK]: {
    destination: string;
  };
  [GA_EVENTS.API_MUTATION_SUCCESS]: {
    method: string;
    endpoint_group: string;
    endpoint_path: string;
    status_code: number;
  };
  [GA_EVENTS.API_MUTATION_FAILURE]: {
    method: string;
    endpoint_group: string;
    endpoint_path: string;
    status_code: number;
  };
};

type RequiredParamsForEvent<T extends GaEventName> =
  T extends keyof RequiredEventParamMap
    ? RequiredEventParamMap[T] & StrictGaEventParams
    : StrictGaEventParams;

const COMMON_ALLOWED_KEYS = ['app', 'page_path', 'flow'] as const;

const AUTH_ALLOWED_KEYS = [
  ...COMMON_ALLOWED_KEYS,
  'provider',
  'status_code',
  'failure_reason',
  'error_code',
  'purpose',
  'source',
  'destination',
  'has_invite_details',
  'has_use_custom_token',
  'is_email_prefilled',
  'email_length',
  'password_length',
  'has_otp_code',
  'has_user_email',
  'method',
  'sign_in_after_signup',
] as const;

const SEARCH_ALLOWED_KEYS = [
  ...COMMON_ALLOWED_KEYS,
  'scope',
  'search_scope',
  'is_scoped_variant',
  'has_query',
  'query_length',
  'module_scope',
  'entity_scope',
  'clicked_element_type',
  'clicked_element_id',
  'has_intent',
] as const;

const ONBOARDING_ALLOWED_KEYS = [
  ...COMMON_ALLOWED_KEYS,
  'country_id',
  'organization_id',
  'step_name',
  'failure_reason',
  'source',
  'destination',
] as const;

const BILLING_ALLOWED_KEYS = [
  ...COMMON_ALLOWED_KEYS,
  'attempt',
  'attempt_number',
  'status_code',
  'will_retry',
  'billing_provider',
  'is_create_organization_flow',
  'is_current_org_admin',
  'has_client_secret',
  'action',
  'plan_name',
  'plan_status',
  'subscription_source',
  'has_payment_method',
  'is_canceled',
  'return_url_present',
  'return_url_pathname',
  'portal_origin',
  'portal_url_present',
  'open_in_new_tab',
  'from_stripe_redirect',
  'checkout_session_present',
  'failure_reason',
] as const;

const ACCOUNT_SETTINGS_ALLOWED_KEYS = [
  ...COMMON_ALLOWED_KEYS,
  'source',
  'destination',
  'step_name',
  'button_name',
  'interaction_type',
  'failure_reason',
  'status_code',
] as const;

const API_MUTATION_ALLOWED_KEYS = [
  ...COMMON_ALLOWED_KEYS,
  'method',
  'endpoint_group',
  'endpoint_path',
  'status_code',
  'latency_bucket_ms',
] as const;

const getAllowedParamKeysForEvent = (eventName: string): readonly string[] => {
  if (eventName.startsWith('auth_')) return AUTH_ALLOWED_KEYS;
  if (eventName.startsWith('search_')) return SEARCH_ALLOWED_KEYS;
  if (eventName.startsWith('onboarding_')) return ONBOARDING_ALLOWED_KEYS;
  if (eventName.startsWith('billing_')) return BILLING_ALLOWED_KEYS;
  if (eventName.startsWith('account_settings_'))
    return ACCOUNT_SETTINGS_ALLOWED_KEYS;
  if (eventName.startsWith('api_mutation_')) return API_MUTATION_ALLOWED_KEYS;
  return [];
};

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

type BrowserWindow = Window &
  typeof globalThis & {
    doNotTrack?: string;
  };

const getBrowserWindow = (): BrowserWindow =>
  globalThis.window as BrowserWindow;

const getCurrentPagePath = () =>
  `${getBrowserWindow().location.pathname}${getBrowserWindow().location.search}${getBrowserWindow().location.hash}`;

const getAppFromHostname = (): 'auth' | 'web' => {
  const host = getBrowserWindow().location.hostname.toLowerCase();
  if (host.startsWith('auth.')) {
    return 'auth';
  }
  return 'web';
};

const inferFlowFromEvent = (eventName: string): string | undefined => {
  if (!eventName.startsWith('auth_')) {
    return undefined;
  }

  if (eventName.startsWith('auth_signin_')) return 'signin';
  if (eventName.startsWith('auth_signup_')) return 'signup';
  if (eventName.startsWith('auth_reset_password_')) return 'reset_password';
  if (eventName.startsWith('auth_invite_')) return 'invite';
  if (eventName.startsWith('auth_otp_')) return 'otp';

  return undefined;
};

const isDoNotTrackEnabled = (): boolean => {
  const browserWindow = getBrowserWindow();
  const legacyNavigator = globalThis.navigator as Navigator & {
    msDoNotTrack?: string;
  };
  const dnt = (
    globalThis.navigator.doNotTrack ||
    browserWindow.doNotTrack ||
    legacyNavigator.msDoNotTrack ||
    ''
  )
    .toString()
    .toLowerCase();

  return dnt === '1' || dnt === 'yes';
};

const getStoredConsent = (): string | null => {
  try {
    return localStorage.getItem(GA_CONSENT_STORAGE_KEY);
  } catch {
    return null;
  }
};

export const isAnalyticsCollectionAllowed = (): boolean => {
  const consent = getStoredConsent();

  if (consent === GA_CONSENT_DENIED) {
    return false;
  }

  if (isDoNotTrackEnabled()) {
    return false;
  }

  return true;
};

const updateGaConsent = (granted: boolean) => {
  const browserWindow = getBrowserWindow();
  if (typeof browserWindow.gtag !== 'function') {
    return;
  }

  browserWindow.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
  });
};

export const setAnalyticsConsent = (granted: boolean) => {
  try {
    localStorage.setItem(
      GA_CONSENT_STORAGE_KEY,
      granted ? GA_CONSENT_GRANTED : GA_CONSENT_DENIED,
    );
  } catch {
    // Ignore localStorage failures in private mode.
  }
  updateGaConsent(granted);
};

export const initializeGoogleAnalytics = (trackingId: string): boolean => {
  if (!trackingId || !isAnalyticsCollectionAllowed()) {
    return false;
  }

  if (!isGaInitialized || initializedTrackingId !== trackingId) {
    ReactGA.initialize(trackingId);
    isGaInitialized = true;
    initializedTrackingId = trackingId;
  }

  updateGaConsent(true);
  return true;
};

const shouldDropParam = (key: string): boolean => {
  const lowered = key.toLowerCase();
  return (
    lowered.includes('password') ||
    lowered.includes('token') ||
    lowered.includes('secret') ||
    lowered.includes('authorization') ||
    lowered.includes('cookie')
  );
};

const sanitizeReferrer = (referrer: string): string => {
  if (!referrer || referrer === 'Direct') {
    return 'Direct';
  }

  try {
    const parsed = new URL(referrer);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    return referrer.split('?')[0].split('#')[0];
  }
};

const sanitizeStringValue = (value: string): string => {
  const compact = value.replace(/\s+/g, ' ').trim();

  // Remove likely emails from analytics payloads.
  const atIndex = compact.indexOf('@');
  const lastAtIndex = compact.lastIndexOf('@');
  const lastDotIndex = compact.lastIndexOf('.');
  const looksLikeEmail =
    atIndex > 0 &&
    atIndex === lastAtIndex &&
    lastDotIndex > atIndex + 1 &&
    lastDotIndex < compact.length - 1 &&
    !compact.includes(' ');

  if (looksLikeEmail) {
    return '[redacted_email]';
  }

  if (compact.length > MAX_STRING_PARAM_LENGTH) {
    return compact.slice(0, MAX_STRING_PARAM_LENGTH);
  }

  return compact;
};

const sanitizeParams = (params: GaEventParams): GaEventParams => {
  const sanitizedEntries = Object.entries(params).flatMap(([key, value]) => {
    if (value === undefined) {
      return [];
    }

    if (shouldDropParam(key)) {
      return [];
    }

    if (typeof value === 'string') {
      if (key === 'source_referrer') {
        return [[key, sanitizeReferrer(value)]];
      }
      return [[key, sanitizeStringValue(value)]];
    }

    return [[key, value]];
  });

  return Object.fromEntries(sanitizedEntries);
};

const enforceAllowedParams = (
  name: string,
  params: GaEventParams,
): GaEventParams => {
  const allowedKeys = getAllowedParamKeysForEvent(name);

  if (allowedKeys.length === 0) {
    return params;
  }

  const allowedKeySet = new Set(allowedKeys);
  const droppedKeys: string[] = [];

  const filteredEntries = Object.entries(params).flatMap(([key, value]) => {
    if (allowedKeySet.has(key)) {
      return [[key, value]];
    }

    droppedKeys.push(key);
    return [];
  });

  if (process.env.NODE_ENV === 'development' && droppedKeys.length > 0) {
    // eslint-disable-next-line no-console
    console.warn('[GA] Dropped disallowed params for event', {
      event: name,
      droppedKeys,
      allowedKeys,
    });
  }

  return Object.fromEntries(filteredEntries);
};

const requiredParamsByPrefix: Array<{
  prefix: string;
  required: string[];
}> = [
  { prefix: 'auth_', required: ['app', 'page_path', 'flow'] },
  { prefix: 'search_', required: ['app', 'page_path'] },
  { prefix: 'onboarding_', required: ['app', 'page_path'] },
  { prefix: 'billing_', required: ['app', 'page_path'] },
  { prefix: 'account_settings_', required: ['app', 'page_path'] },
  {
    prefix: 'api_mutation_',
    required: [
      'app',
      'page_path',
      'method',
      'endpoint_group',
      'endpoint_path',
      'status_code',
    ],
  },
];

const validateRequiredParams = (name: string, params: GaEventParams) => {
  if (process.env.NODE_ENV !== 'development') {
    return;
  }

  const contract = requiredParamsByPrefix.find(({ prefix }) =>
    name.startsWith(prefix),
  );

  if (!contract) {
    return;
  }

  const missing = contract.required.filter(
    (paramName) => !(paramName in params),
  );
  if (missing.length > 0) {
    // eslint-disable-next-line no-console
    console.warn('[GA] Missing required params for event', {
      event: name,
      missing,
      params,
    });
  }
};

const emitRecommendedGaEvent = (name: string, params: GaEventParams) => {
  if (!isGaInitialized || !isAnalyticsCollectionAllowed()) {
    return;
  }

  if (name === GA_EVENTS.AUTH_SIGNIN_PASSWORD_SUCCESS) {
    ReactGA.event('login', {
      method: 'email_password',
      app: params.app,
    });
  }

  if (name === GA_EVENTS.AUTH_SIGNUP_PASSWORD_SUCCESS) {
    ReactGA.event('sign_up', {
      method: 'email_password',
      app: params.app,
    });
  }

  if (name === GA_EVENTS.ONBOARDING_ORG_SETUP_SUBMIT) {
    ReactGA.event('generate_lead', {
      app: params.app,
    });
  }

  if (name === GA_EVENTS.SEARCH_QUERY_SUBMITTED) {
    ReactGA.event('view_search_results', {
      app: params.app,
      query_length: params.query_length,
      search_scope: params.search_scope ?? params.scope,
    });
  }

  if (name === GA_EVENTS.SEARCH_RESULT_CLICKED) {
    ReactGA.event('select_item', {
      app: params.app,
      item_id: params.clicked_element_id,
      item_list_name: params.module_scope ?? 'search_results',
    });
  }

  if (name === GA_EVENTS.BILLING_PLANS_VIEWED) {
    ReactGA.event('view_item_list', {
      app: params.app,
      item_list_name: 'plans',
    });
  }

  if (name === GA_EVENTS.BILLING_CUSTOMER_SESSION_ATTEMPT) {
    ReactGA.event('begin_checkout', {
      app: params.app,
    });
  }
};

// Track user source (e.g., referral or direct access)
export const trackUserSource = () => {
  trackGaEvent('user_source', {
    source_referrer: document.referrer || 'Direct',
  });
};

export const trackButtonClick = (buttonName: string) => {
  trackGaEvent('button_click', {
    button_name: buttonName,
    interaction_type: 'click',
  });
};

type TrackGaEventFn = {
  <T extends GaEventName>(name: T, params: RequiredParamsForEvent<T>): void;
  (name: string, params?: GaEventParams): void;
};

export const trackGaEvent: TrackGaEventFn = (
  name: GaEventName | string,
  params: GaEventParams = {},
) => {
  if (!isGaInitialized || !isAnalyticsCollectionAllowed()) {
    return;
  }

  const withDefaults: GaEventParams = {
    app: getAppFromHostname(),
    page_path: getCurrentPagePath(),
    ...params,
  };

  const inferredFlow = inferFlowFromEvent(name);
  if (inferredFlow && !withDefaults.flow) {
    withDefaults.flow = inferredFlow;
  }

  // Provide canonical key for search scope when legacy call-sites use "scope".
  if (
    name === GA_EVENTS.SEARCH_SCOPE_CHANGED &&
    typeof withDefaults.scope === 'string' &&
    !withDefaults.search_scope
  ) {
    withDefaults.search_scope = withDefaults.scope;
  }

  const sanitizedParams: GaEventParams = sanitizeParams(withDefaults);
  const normalizedParams = enforceAllowedParams(name, sanitizedParams);
  validateRequiredParams(name, normalizedParams);
  ReactGA.event(name, normalizedParams);
  emitRecommendedGaEvent(name, normalizedParams);
};

export const trackPageView = (path = getCurrentPagePath()) => {
  if (!isGaInitialized || !isAnalyticsCollectionAllowed()) {
    return;
  }

  if (!path || path === lastTrackedPagePath) {
    return;
  }

  lastTrackedPagePath = path;
  ReactGA.send({ hitType: 'pageview', page: path });
};

export const initializePageViewTracking = () => {
  const browserWindow = getBrowserWindow();
  const pushState: History['pushState'] = browserWindow.history.pushState.bind(
    browserWindow.history,
  );
  const replaceState: History['replaceState'] =
    browserWindow.history.replaceState.bind(browserWindow.history);

  const emitPageView = () => {
    trackPageView();
  };

  browserWindow.history.pushState = function pushStateWithTracking(
    ...args: Parameters<History['pushState']>
  ) {
    const result = pushState(...args);
    emitPageView();
    return result;
  };

  browserWindow.history.replaceState = function replaceStateWithTracking(
    ...args: Parameters<History['replaceState']>
  ) {
    const result = replaceState(...args);
    emitPageView();
    return result;
  };

  globalThis.addEventListener('popstate', emitPageView);
  globalThis.addEventListener('hashchange', emitPageView);
  emitPageView();

  return () => {
    browserWindow.history.pushState = pushState;
    browserWindow.history.replaceState = replaceState;
    globalThis.removeEventListener('popstate', emitPageView);
    globalThis.removeEventListener('hashchange', emitPageView);
  };
};

const getExceptionDescription = (reason: unknown): string => {
  if (reason instanceof Error && typeof reason.message === 'string') {
    return reason.message;
  }

  if (typeof reason === 'string') {
    return reason;
  }

  return 'unknown_error';
};

export const initializeGaExceptionTracking = () => {
  if (!isGaInitialized || !isAnalyticsCollectionAllowed()) {
    return undefined;
  }

  if (gaExceptionTrackingCleanup) {
    return gaExceptionTrackingCleanup;
  }

  const sendException = (description: string) => {
    ReactGA.event('exception', {
      description: sanitizeStringValue(description),
      fatal: false,
      app: getAppFromHostname(),
      page_path: getCurrentPagePath(),
    });
  };

  const onError = (event: ErrorEvent) => {
    if (!event.message) {
      return;
    }

    sendException(`js_error:${event.message}`);
  };

  const onUnhandledRejection = (event: PromiseRejectionEvent) => {
    const reason = getExceptionDescription(event.reason);
    sendException(`unhandled_rejection:${reason}`);
  };

  globalThis.addEventListener('error', onError);
  globalThis.addEventListener('unhandledrejection', onUnhandledRejection);

  gaExceptionTrackingCleanup = () => {
    globalThis.removeEventListener('error', onError);
    globalThis.removeEventListener('unhandledrejection', onUnhandledRejection);
    gaExceptionTrackingCleanup = null;
  };

  return gaExceptionTrackingCleanup;
};

export const setUserDetails = (
  userId: string | null,
  tenantId: string | null,
) => {
  if (!isGaInitialized || !isAnalyticsCollectionAllowed()) {
    return;
  }

  const { hostname } = getBrowserWindow().location;

  const subdomain = (() => {
    if (hostname === 'localhost') {
      return 'localhost';
    }

    const parts = hostname.split('.');

    if (parts.length === 3) {
      const [subdomainPart] = parts;

      if (subdomainPart === 'app') {
        return 'webapp';
      }
      if (subdomainPart === 'auth') {
        return 'auth';
      }

      return subdomainPart;
    }

    return '';
  })();

  ReactGA.set({
    environment: process.env.NODE_ENV,
    platform,
    app_version: appVersion,
    subdomain,
    tenant_id: tenantId,
    backend: `${process.env.NODE_ENV}-backend`,
    user_id: userId,
  });
};
